import React, {useEffect, useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CartClaimButton from '../components/CartClaimButton';
import {leaseDynamicSlot} from '../hooks/useSlotLease';

const {FiActivity, FiCheck, FiLock, FiShield, FiClock} = FiIcons;

const benefits = [
  'Licensed clinician review',
  'Care tailored to your goals',
  'Pharmacy delivery if prescribed'
];

export default function WeightCart() {
  const [leasedSlot, setLeasedSlot] = useState(null);
  const [slotError, setSlotError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);

    const reserveSlot = async () => {
      const result = await leaseDynamicSlot(
        localStorage.getItem('glitchy_mock_backend') === 'true'
      );

      if (result.error) {
        setSlotError(result.error);
        return;
      }

      setLeasedSlot(result.slot);
    };

    reserveSlot();
  }, []);

  const beginIntake = () => {
    const intakeUrl = new URL(
      'https://medical.leguprecovery.com/start-online-visit/sema-personalized-intake-vialsrx'
    );

    intakeUrl.searchParams.set('partner_id', 'GJQPQTLQ');

    if (leasedSlot !== null && leasedSlot !== undefined) {
      intakeUrl.searchParams.set('sub', String(leasedSlot));
    }

    window.location.href = intakeUrl.toString();
  };

  return (
    <main className="min-h-screen bg-[#eaf6f7] px-4 py-5 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-5xl overflow-hidden rounded-[2rem] border border-white/80 bg-white shadow-2xl shadow-slate-900/10">
        <div className="flex items-center justify-center gap-2 border-b border-amber-200 bg-gradient-to-r from-amber-50 via-[#fff9e8] to-amber-50 px-5 py-4 text-center">
          <SafeIcon icon={FiClock} className="h-4 w-4 shrink-0 text-amber-700" />
          <p className="text-sm font-extrabold tracking-wide text-amber-900 sm:text-base">
            Your 35% savings is reserved
          </p>
        </div>

        <div className="lg:grid lg:grid-cols-[44%_56%]">
          <section className="relative min-h-[330px] overflow-hidden lg:min-h-[720px]">
            <img
              src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=1200&q=85"
              alt="Licensed doctor providing care"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#003d67]/95 via-[#064b73]/35 to-[#003d67]/5" />

            <div className="absolute bottom-0 p-7 text-white sm:p-10">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/20 bg-white/15 backdrop-blur-md">
                <SafeIcon icon={FiShield} className="h-6 w-6" />
              </div>
              <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-teal-100">
                Weight loss care
              </p>
              <h1 className="mt-3 max-w-sm text-3xl font-extrabold leading-tight sm:text-4xl">
                Care from a licensed clinician.
              </h1>
              <p className="mt-4 max-w-md text-base leading-relaxed text-blue-50">
                Start your private online intake from home.
              </p>
            </div>
          </section>

          <section className="flex items-center justify-center px-5 py-9 sm:px-10 lg:px-12">
            <motion.div
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.55}}
              className="w-full max-w-md"
            >
              <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-600 ring-8 ring-teal-50/60">
                <SafeIcon icon={FiActivity} className="h-6 w-6" />
              </div>

              <p className="text-center text-xs font-extrabold uppercase tracking-[0.22em] text-teal-700">
                Your offer is waiting
              </p>
              <h2 className="mt-4 text-center text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                Continue your care journey.
              </h2>
              <p className="mx-auto mt-3 max-w-sm text-center text-base leading-relaxed text-slate-600">
                Complete a quick intake to explore your options.
              </p>

              <div className="mt-6">
                <CartClaimButton onClick={beginIntake} disabled={Boolean(slotError)} compact />
              </div>

              <div className="mt-6 rounded-2xl border border-teal-100 bg-gradient-to-br from-teal-50 to-cyan-50 p-5">
                <div className="flex items-center justify-center gap-2 text-2xl font-extrabold text-teal-700">
                  <SafeIcon icon={FiActivity} className="h-6 w-6" />
                  Save 35% Today
                </div>

                <ul className="mt-5 space-y-3">
                  {benefits.map((benefit) => (
                    <li
                      key={benefit}
                      className="flex items-start gap-3 text-sm font-medium leading-relaxed text-slate-700"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white">
                        <SafeIcon icon={FiCheck} className="h-3.5 w-3.5" />
                      </span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6">
                <CartClaimButton onClick={beginIntake} disabled={Boolean(slotError)} />
              </div>

              {slotError && (
                <p className="mt-3 text-center text-sm font-medium text-red-600">
                  {slotError}
                </p>
              )}

              <p className="mt-5 flex items-center justify-center gap-2 text-center text-sm text-slate-500">
                <SafeIcon icon={FiLock} className="h-4 w-4" />
                Secure, private intake
              </p>
              <p className="mt-3 text-center text-xs leading-relaxed text-slate-400">
                Eligibility and prescriptions are determined by a licensed clinician.
              </p>
            </motion.div>
          </section>
        </div>
      </div>
    </main>
  );
}